import { useState, useEffect, useMemo } from "react";
import { useBookingWizard } from "../../../contexts/BookingWizardContext";
import { useTariffs, Tariff } from "../../../hooks/useTariffs";
import { useTransportTypes } from "../../../hooks/useTransportTypes";
import { useIncoterms } from "../../../hooks/useIncoterms";
import { useSafeProductForm } from "../PricingCalculatorHelpers";
import dayjs from "dayjs";
import {
  CUSTOMS_CLEARANCE,
  HANDLING,
  DELIVERY,
  AIR_FREIGHT,
} from "../../../lib/pricingDefaults";
import { CalculatedPrices, RouteOption } from "../PricingCalculatorTypes";
import { calculatePricingFromTemplates } from "../../../services/unifiedPricingService";

export const usePricingCalculation = () => {
  const { state } = useBookingWizard();
  const { tariffs } = useTariffs();
  const { transportTypes } = useTransportTypes();
  const { incoterms } = useIncoterms();
  const { currentFormValues, isEditingProduct } = useSafeProductForm();

  const [selectedOriginPortId, setSelectedOriginPortId] = useState<number | null>(null);
  const [selectedDestinationPortId, setSelectedDestinationPortId] = useState<number | null>(null);
  const [selectedTariffId, setSelectedTariffId] = useState<number | null>(null);

  const [calculatedPrices, setCalculatedPrices] = useState<CalculatedPrices>({
    pricePerKg: 0,
    volumeWeight: 0,
    chargeableWeight: 0,
    transportCost: 0,
    customsClearance: 0,
    handlingFees: 0,
    deliveryCost: 0,
    totalCost: 0,
    totalDeliveryCost: 0,
    dieselSurcharge: 0,
    pickupFee: 0,
    exportClearanceLicense: 0,
  });

  const [applicableTariff, setApplicableTariff] = useState<Tariff | null>(null);

  const transportType = transportTypes.find((t) => t.id === state.transport_type)?.name || "";
  const incoterm = incoterms.find((i) => i.id === state.booking_incoterm)?.name || "";

  // Create combined route options for the dropdown
  const routeOptions = useMemo(() => {
    if (!state.transport_type) return [];

    const availableTariffs = tariffs.filter((tariff) => {
      const transportTypeMatches = tariff.transport_type.id === state.transport_type;

      if (state.transport_type === 2 && state.booking_type) {
        const isFcl = state.booking_type === 1;
        const expectedBookingsTypeId = isFcl ? 1 : 2;
        return transportTypeMatches && tariff.bookings_type?.id === expectedBookingsTypeId;
      }

      return transportTypeMatches;
    });

    const routeGroups: Record<string, Tariff[]> = {};

    availableTariffs.forEach((tariff) => {
      const routeKey = `${tariff.origin_port.id}-${tariff.destination_port.id}`;
      if (!routeGroups[routeKey]) {
        routeGroups[routeKey] = [];
      }
      routeGroups[routeKey].push(tariff);
    });

    return Object.keys(routeGroups).map((routeKey) => {
      const tariffsInRoute = routeGroups[routeKey];
      const firstTariff = tariffsInRoute[0];

      const validTariffs = tariffsInRoute
        .filter((t) => !t.to || dayjs(t.to).isAfter(dayjs()))
        .sort((a, b) => {
          if (!a.from) return 1;
          if (!b.from) return -1;
          return dayjs(b.from).valueOf() - dayjs(a.from).valueOf();
        });

      const currentTariff = validTariffs.length > 0 ? validTariffs[0] : firstTariff;

      let label = `${firstTariff.origin_port.name} → ${firstTariff.destination_port.name}`;

      if (state.transport_type === 1 && currentTariff.price) {
        label += ` (€${currentTariff.price.toFixed(2)}/kg)`;
      } else if (state.transport_type === 2 && currentTariff.price) {
        if (currentTariff.bookings_type?.id === 2) {
          label += ` (€${currentTariff.price.toFixed(2)}/container)`;
        } else {
          label += ` (€${currentTariff.price.toFixed(2)}/cbm)`;
        }
      }

      return {
        value: routeKey,
        label,
        originPortId: firstTariff.origin_port.id,
        destinationPortId: firstTariff.destination_port.id,
        tariffId: currentTariff.id,
      } as RouteOption;
    });
  }, [state.transport_type, state.booking_type, tariffs]);

  // Auto-select first route when options change
  useEffect(() => {
    if (routeOptions.length > 0 && !selectedOriginPortId && !selectedDestinationPortId) {
      const firstRoute = routeOptions[0];
      if (firstRoute) {
        setSelectedOriginPortId(firstRoute.originPortId);
        setSelectedDestinationPortId(firstRoute.destinationPortId);
        setSelectedTariffId(firstRoute.tariffId);
      }
    }
  }, [routeOptions, selectedOriginPortId, selectedDestinationPortId]);

  // Handle route selection
  const handleRouteChange = (routeValue: string) => {
    const selectedRoute = routeOptions.find((route) => route.value === routeValue);
    if (selectedRoute) {
      setSelectedOriginPortId(selectedRoute.originPortId);
      setSelectedDestinationPortId(selectedRoute.destinationPortId);
      setSelectedTariffId(selectedRoute.tariffId);
    }
  };

  // Calculate costs whenever relevant booking details change
  useEffect(() => {
    const calculateCosts = async () => {
      const storedProducts = state.products || [];
      let allProducts = [...storedProducts];

      if (isEditingProduct && currentFormValues) {
        const hasFormValues =
          currentFormValues.gross_weight ||
          currentFormValues.cbm ||
          currentFormValues.value ||
          currentFormValues.pieces ||
          currentFormValues.cartons;

        if (hasFormValues) {
          allProducts = [
            ...storedProducts,
            {
              name: currentFormValues.name || "New Product",
              hs_code: currentFormValues.hs_code || null,
              gross_weight: currentFormValues.gross_weight || 0,
              cbm: currentFormValues.cbm || 0,
              value: currentFormValues.value || 0,
              pieces: currentFormValues.pieces || 0,
              cartons: currentFormValues.cartons || 0,
            },
          ];
        }
      }

      const transportTypeId = state.transport_type;
      const incotermId = state.booking_incoterm;

      const totalWeight = allProducts.reduce((sum, product) => sum + (product.gross_weight || 0), 0);
      const totalCBM = allProducts.reduce((sum, product) => sum + (product.cbm || 0), 0);
      const totalCartons = allProducts.reduce((sum, product) => sum + (product.cartons || 0), 0);

      const uniqueHsCodes = new Set(
        allProducts.map((product) => product.hs_code).filter((hsCode) => hsCode)
      );

      const uniqueHsCodesCount = uniqueHsCodes.size;
      const selectedTariff = selectedTariffId && tariffs.find((t) => t.id === selectedTariffId);
      const isStorageDelivery = state.delivery_type === "storage";

      // Use the unified pricing service
      const pricingContext = {
        transportTypeId,
        bookingTypeId: state.booking_type,
        incotermId,
        originPortId: selectedOriginPortId || undefined,
        destinationPortId: selectedDestinationPortId || undefined,
        containerSize: undefined, // Will be determined from booking context if needed
        matchDate: new Date().toISOString().split('T')[0],
      };

      const bookingData = {
        totalCartons,
        totalPallets: Math.ceil(totalCartons / 8), // Assume 8 cartons per pallet
        uniqueProducts: uniqueHsCodesCount,
        totalWeight,
        totalVolume: totalCBM,
        uniqueHsCodesCount,
        totalValue: undefined, // Not available in wizard
        containerSize: undefined,
      };

      const result = await calculatePricingFromTemplates(
        pricingContext,
        bookingData,
        {
          selectedTariff,
          pickupFee: state.pickupFee,
          includeExportClearanceLicense: state.includeExportClearanceLicense,
          isStorageDelivery,
          formatEuro: (value: number) => `€${value.toFixed(2)}`,
          t: (key: string) => key, // Placeholder translation function
          filterZeroComponents: false,
        }
      );

      if (result.error) {
        console.error("Pricing calculation error:", result.error);
        setCalculatedPrices({
          pricePerKg: 0,
          volumeWeight: 0,
          chargeableWeight: 0,
          transportCost: 0,
          customsClearance: 0,
          handlingFees: 0,
          deliveryCost: 0,
          totalCost: 0,
          totalDeliveryCost: 0,
          dieselSurcharge: 0,
          pickupFee: state.pickupFee || 0,
          exportClearanceLicense: state.includeExportClearanceLicense ? 45 : 0,
        });
        return;
      }

      // Apply step-based filtering for wizard (calculator tool will show all costs)
      const totalCost =
        result.totalCost +
        (state.currentStep >= CUSTOMS_CLEARANCE.DISPLAY_FROM_STEP ? 0 : 0) + // Customs clearance is now included in unified service
        (state.currentStep >= HANDLING.DISPLAY_FROM_STEP ? 0 : 0) + // Handling is now included in unified service
        (state.currentStep >= DELIVERY.DISPLAY_FROM_STEP ? 0 : 0) + // Delivery is now included in unified service
        (state.currentStep >= AIR_FREIGHT.DISPLAY_FROM_STEP ? 0 : 0); // All costs are now included in unified service

      // Extract transport cost from components for backward compatibility
      const transportComponent = result.components.find(c =>
        c.name.toLowerCase().includes('transport') ||
        c.name.toLowerCase().includes('freight')
      );
      const transportCost = transportComponent?.totalPrice || 0;

      // Extract diesel surcharge from components
      const dieselComponent = result.components.find(c =>
        c.name.toLowerCase().includes('diesel')
      );
      const dieselSurcharge = dieselComponent?.totalPrice || 0;

      setCalculatedPrices({
        pricePerKg: 0, // Not directly available from unified service
        volumeWeight: totalCBM * 167, // Approximate
        chargeableWeight: Math.max(totalWeight, totalCBM * 167),
        transportCost,
        customsClearance: 0, // Included in unified service
        handlingFees: 0, // Included in unified service
        deliveryCost: 0, // Included in unified service
        totalCost,
        totalDeliveryCost: 0,
        dieselSurcharge,
        pickupFee: state.pickupFee || 0,
        exportClearanceLicense: state.includeExportClearanceLicense ? 45 : 0,
      });

      setApplicableTariff(selectedTariff || null);
    };

    calculateCosts().catch((error) => {
      console.error("Error calculating pricing:", error);
    });
  }, [
    state,
    state.transport_type,
    state.products,
    state.currentStep,
    state.booking_incoterm,
    state.pickupFee,
    state.includeExportClearanceLicense,
    state.delivery_type,
    tariffs,
    isEditingProduct,
    currentFormValues,
    selectedTariffId,
  ]);

  return {
    calculatedPrices,
    applicableTariff,
    routeOptions,
    selectedOriginPortId,
    selectedDestinationPortId,
    selectedTariffId,
    handleRouteChange,
    transportType,
    incoterm,
    isEditingProduct,
    currentFormValues,
  };
};






